import type { TFunction } from 'i18next';

import { EGoalType, EGoalTypeV1 } from '@feathr/blackbox';
import type { ISorted } from '@feathr/components';

export interface IGoalRow {
  goalId: string;
  name: string;
  type: EGoalType | EGoalTypeV1;
  numConversions: number;
  sharePercent: number;
}

/**
 * Human label for a goal's `conv_type`. Enum values come from anhinga as raw
 * strings (e.g. `'donation'`, `'page_view'`) which aren't usable as i18n keys
 * directly, so we map each one to a localized phrase.
 */
export function goalTypeLabel(t: TFunction, type: EGoalType | EGoalTypeV1): string {
  switch (type) {
    case EGoalType.page_view:
      return t('Page view');

    case EGoalType.group_entry:
      return t('Group entry');

    case EGoalType.conversion_pixel:
      return t('Conversion pixel');

    case EGoalType.donation_form_submission:
      return t('Donation');

    case EGoalType.form_submission:
      return t('Form submission');

    case EGoalTypeV1.ad_click:
      return t('Ad click');

    case EGoalTypeV1.custom:
      return t('Custom');
  }
}

/**
 * Share of total conversions as an integer 0-100. Returns 0 for any invalid
 * input (zero/negative totals or values) so callers can safely format `${pct}%`.
 */
export function formatShare(value: number, total: number): number {
  if (total <= 0 || value <= 0) {
    return 0;
  }
  const pct = Math.round((value / total) * 100);
  return Math.min(pct, 100);
}

type TGoalRowSortKey = 'name' | 'type' | 'numConversions' | 'sharePercent';

const SORT_KEYS: readonly TGoalRowSortKey[] = ['name', 'type', 'numConversions', 'sharePercent'];

const DEFAULT_SORT: ISorted[] = [{ id: 'numConversions', desc: true }];

function isSortKey(id: string): id is TGoalRowSortKey {
  return (SORT_KEYS as readonly string[]).includes(id);
}

export function sortGoalRows(rows: IGoalRow[], sort: ISorted[]): IGoalRow[] {
  const active = sort.length > 0 ? sort : DEFAULT_SORT;
  const { id, desc } = active[0];
  if (!isSortKey(id)) {
    return [...rows];
  }
  const direction = desc ? -1 : 1;
  return [...rows].sort((a, b) => {
    const aVal = a[id];
    const bVal = b[id];
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return aVal.localeCompare(bVal) * direction;
    }
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return (aVal - bVal) * direction;
    }
    return 0;
  });
}
